/**
 * ObjectiveCorrectnessAnalysis — schema view for `ObjectiveCorrectnessV1`.
 * Composes the same sections as the freeform view minus the rating chips
 * (objective schemas don't emit content / reasoning categorical labels).
 * Approach summary + visual checks are admin-audience only.
 *
 * Tutor-feedback v2 (behind `analysis_view_v2`): the verdict + per-axis
 * rationale, mirroring the freeform view. Objective emits
 * `correctness_rationale`/`approach_rationale`, mapped to the shared
 * accuracy/reasoning transport, so the same `RationaleList` renders them.
 */
import type { Feedback } from '@padcanvas/shared';
import { VerdictBlock } from '../sections/VerdictBlock';
import { RationaleList } from '../sections/RationaleList';
import { DiagnosisBlock } from '../sections/DiagnosisBlock';
import { diagnosisForStudent } from '../sections/diagnosisLabels';
import { GatedNote } from '../sections/GatedNote';
import { StepsList } from '../sections/StepsList';
import { InterpretationBlock } from '../sections/InterpretationBlock';
import { VisualChecksList } from '../sections/VisualChecksList';
import { SuggestionsList } from '../sections/SuggestionsList';
import { HintsList } from '../sections/HintsList';
import { AdminOnlySection } from '../sections/AdminOnlySection';
import type { SolutionAnalysisAudience } from '../audience';

interface Props {
    feedback: Feedback;
    audience?: SolutionAnalysisAudience;
    /** Show the tutor-feedback v2 surfaces (verdict, per-axis rationale).
     *  Defaults to shown; the student app passes the `analysis_view_v2` gate. */
    analysisV2?: boolean;
    /** Question type, for rationale suppression on choice questions
     *  (see RationaleList). Optional — unknown keeps every rationale. */
    questionType?: string | null;
}

export function ObjectiveCorrectnessAnalysis({ feedback, audience = 'admin', analysisV2, questionType }: Props) {
    const a = feedback.llmAssessment;
    const showAdminOnly = audience === 'admin';
    const showV2 = analysisV2 ?? true;
    return (
        <>
            {showV2 && (
                <>
                    {audience === 'admin' && <GatedNote gate="analysis_view_v2" />}
                    <VerdictBlock verdict={a?.verdict} />
                    <RationaleList
                        accuracyRationale={a?.accuracyRationale}
                        reasoningRationale={a?.reasoningRationale}
                        accuracyScore={feedback.computedScores?.correctnessScore}
                        reasoningScore={feedback.computedScores?.approachScore}
                        questionType={questionType}
                    />
                </>
            )}
            {/* Diagnosis (named cause + slip/gap) is admin-only — hidden from
                students, shown in admin/teacher review + parsed views. */}
            {/* Mounted only with a diagnosis to show: the heading is unconditional,
                and a withheld diagnosis (selection gate) would leave it bare. */}
            {showAdminOnly && diagnosisForStudent(a?.diagnosis) && (
                <AdminOnlySection title="Diagnosis">
                    <DiagnosisBlock diagnosis={a?.diagnosis} />
                </AdminOnlySection>
            )}
            <StepsList steps={a?.stepsAnalysis ?? []} title="Step analysis" collapsible richSteps={showV2} lowTrust={feedback.trust?.level === 'low'} />
            {showAdminOnly && a?.interpretation?.approachSummary && (
                <AdminOnlySection>
                    <InterpretationBlock interpretation={a.interpretation} />
                </AdminOnlySection>
            )}
            {showAdminOnly && a?.visualChecks && a.visualChecks.length > 0 && (
                <AdminOnlySection>
                    <VisualChecksList items={a.visualChecks} />
                </AdminOnlySection>
            )}
            <HintsList items={a?.solutionGuidance} />
            <SuggestionsList items={a?.suggestionsGeneral} />
        </>
    );
}
